import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, getDocs, addDoc, collection, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { encryptPatientPayload } from "../utils/encryption";
import "./PatientFormPage.css";

function PatientFormPage() {
  const { token } = useParams();
  const [form, setForm] = useState({
    name: "",
    age: "",
    gender: "Male",
    phone: "",
    email: "",
    address: "",
    complaint: "",
    history: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [formLink, setFormLink] = useState(null);

  useEffect(() => {
    const loadFormLink = async () => {
      try {
        if (!token) {
          setError("Invalid form link");
          setLoading(false);
          return;
        }

        // Find the form link document
        const formLinksRef = collection(db, "patientFormLinks");
        const formLinksSnap = await getDocs(formLinksRef);
        let foundLink = null;

        formLinksSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.token === token) {
            foundLink = { id: docSnap.id, ...data };
          }
        });

        if (!foundLink) {
          setError("Form link not found or has expired");
          setLoading(false);
          return;
        }

        setFormLink(foundLink);

        // Load doctor info
        if (foundLink.userId) {
          const userDoc = await getDoc(doc(db, "users", foundLink.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Get the doctor's full name - check all possible fields
            // displayName is used in settings, fullName is used during registration
            const doctorName = (userData.displayName && userData.displayName.trim()) || 
                              (userData.fullName && userData.fullName.trim()) || 
                              (userData.name && userData.name.trim()) || 
                              "Doctor";
            setDoctorInfo({
              name: doctorName,
              clinic: userData.clinic || "",
            });
          } else {
            // If user doc doesn't exist, set default
            setDoctorInfo({
              name: "Doctor",
              clinic: "",
            });
          }
        }
      } catch (e) {
        console.error("Failed to load form link", e);
        setError("Failed to load form. Please check the link and try again.");
      } finally {
        setLoading(false);
      }
    };

    loadFormLink();
  }, [token]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim() || !form.age || !form.phone.trim()) {
      setError("Please fill in all required fields (Name, Age, Phone)");
      return;
    }

    if (!formLink || !formLink.userId) {
      setError("Invalid form link");
      return;
    }

    setSaving(true);
    try {
      // Create patient record
      await addDoc(
        collection(db, "patients"),
        encryptPatientPayload({
          name: form.name.trim(),
          age: form.age,
          gender: form.gender,
          phone: form.phone.trim(),
          email: form.email.trim() || "",
          address: form.address.trim() || "",
          complaint: form.complaint.trim() || "",
          history: form.history.trim() || "",
          status: "New",
          userId: formLink.userId,
          createdAt: serverTimestamp(),
          source: "form_link", // Track that patient came from form link
          formLinkToken: token,
        }),
      );

      // Update form link usage count
      if (formLink.id) {
        await updateDoc(doc(db, "patientFormLinks", formLink.id), {
          usageCount: (formLink.usageCount || 0) + 1,
          lastUsedAt: serverTimestamp(),
        });
      }

      setSuccess(true);
    } catch (err) {
      console.error("Failed to submit form", err);
      setError(err.message || "Failed to submit form. Please try again.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="patient-form-layout">
        <div className="patient-form-container">
          <div className="patient-form-loading">
            <span className="material-symbols-outlined patient-form-loading-icon">hourglass_empty</span>
            <p>Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !formLink) {
    return (
      <div className="patient-form-layout">
        <div className="patient-form-container">
          <div className="patient-form-error">
            <span className="material-symbols-outlined patient-form-error-icon">error</span>
            <h3>Form Not Found</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="patient-form-layout">
        <div className="patient-form-container">
          <div className="patient-form-success">
            <span className="material-symbols-outlined patient-form-success-icon">check_circle</span>
            <h3>Form Submitted Successfully!</h3>
            <p>
              Thank you for submitting your information. {doctorInfo?.name ? `${doctorInfo.name} will` : "The doctor will"} review your details and contact you soon.
            </p>
            <p className="patient-form-success-note">You can close this page now.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-form-layout">
      <div className="patient-form-container">
        <div className="patient-form-header">
          <h1>Patient Registration Form</h1>
        </div>

        <form className="patient-form" onSubmit={handleSubmit}>
          {error && (
            <div className="patient-form-error-message">
              <span className="material-symbols-outlined">error</span>
              <span>{error}</span>
            </div>
          )}

          <div className="patient-form-section">
            <h2 className="patient-form-section-title">Personal Information</h2>
            
            <div className="patient-form-field">
              <label className="patient-form-label">
                Full Name <span className="patient-form-required">*</span>
              </label>
              <div className="patient-form-input-wrapper">
                <span className="material-symbols-outlined patient-form-input-icon">person</span>
                <input
                  type="text"
                  className="patient-form-input"
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={handleChange("name")}
                  required
                />
              </div>
            </div>

            <div className="patient-form-row">
              <div className="patient-form-field patient-form-field-half">
                <label className="patient-form-label">
                  Age <span className="patient-form-required">*</span>
                </label>
                <div className="patient-form-input-wrapper">
                  <span className="material-symbols-outlined patient-form-input-icon">cake</span>
                  <input
                    type="number"
                    className="patient-form-input"
                    placeholder="Age"
                    value={form.age}
                    onChange={handleChange("age")}
                    min="0"
                    max="150"
                    required
                  />
                </div>
              </div>

              <div className="patient-form-field patient-form-field-half">
                <label className="patient-form-label">
                  Gender <span className="patient-form-required">*</span>
                </label>
                <div className="patient-form-input-wrapper">
                  <span className="material-symbols-outlined patient-form-input-icon">people</span>
                  <select
                    className="patient-form-select"
                    value={form.gender}
                    onChange={handleChange("gender")}
                    required
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="patient-form-field">
              <label className="patient-form-label">
                Phone Number <span className="patient-form-required">*</span>
              </label>
              <div className="patient-form-input-wrapper">
                <span className="material-symbols-outlined patient-form-input-icon">call</span>
                <input
                  type="tel"
                  className="patient-form-input"
                  placeholder="Enter your phone number"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  required
                />
              </div>
            </div>

            <div className="patient-form-field">
              <label className="patient-form-label">Email Address</label>
              <div className="patient-form-input-wrapper">
                <span className="material-symbols-outlined patient-form-input-icon">mail</span>
                <input
                  type="email"
                  className="patient-form-input"
                  placeholder="Enter your email (optional)"
                  value={form.email}
                  onChange={handleChange("email")}
                />
              </div>
            </div>

            <div className="patient-form-field">
              <label className="patient-form-label">Address</label>
              <div className="patient-form-input-wrapper">
                <span className="material-symbols-outlined patient-form-input-icon">location_on</span>
                <input
                  type="text"
                  className="patient-form-input"
                  placeholder="Enter your address (optional)"
                  value={form.address}
                  onChange={handleChange("address")}
                />
              </div>
            </div>
          </div>

          <div className="patient-form-section">
            <h2 className="patient-form-section-title">Medical Information</h2>
            
            <div className="patient-form-field">
              <label className="patient-form-label">Chief Complaint / Reason for Visit</label>
              <div className="patient-form-textarea-wrapper">
                <span className="material-symbols-outlined patient-form-textarea-icon">sick</span>
                <textarea
                  className="patient-form-textarea"
                  placeholder="Describe your chief complaint or reason for visit (optional)"
                  value={form.complaint}
                  onChange={handleChange("complaint")}
                  rows={4}
                />
              </div>
            </div>

            <div className="patient-form-field">
              <label className="patient-form-label">Medical History</label>
              <div className="patient-form-textarea-wrapper">
                <span className="material-symbols-outlined patient-form-textarea-icon">history</span>
                <textarea
                  className="patient-form-textarea"
                  placeholder="Previous surgeries, medications, allergies, etc. (optional)"
                  value={form.history}
                  onChange={handleChange("history")}
                  rows={4}
                />
              </div>
            </div>
          </div>

          <div className="patient-form-actions">
            <button type="submit" className="patient-form-submit" disabled={saving}>
              {saving ? (
                <>
                  <span className="loading-spinner patient-form-spinner"></span>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">send</span>
                  <span>Submit Form</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PatientFormPage;
